import { useTranslation } from "react-i18next";
import type { BuilderValidationIssue } from "../../lib/metadata-builder/validate-metadata";
import type { ArtMetadata } from "../../lib/schedule-presets/art-metadata";
import { metadataTargets } from "../../lib/metadata-history/metadata-targets";
import { metadataTargetLabel } from "../preview-canvas/metadata-target-label";
import { useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import { metadataLabels } from "../editor-panel/developer-section/metadata-form-schema";
import { Button } from "../ui/button";

type ReviewValidationProps = { metadata: ArtMetadata; issues: BuilderValidationIssue[] };

export function ReviewValidation({ metadata, issues }: ReviewValidationProps) {
  const { t } = useTranslation();
  const labels = metadataLabels(t);
  const targets = metadataTargets(metadata);
  const steps = {
    upload: t("builder.step.upload"), credit: t("builder.step.credit"), dates: t("builder.step.dates"),
    offline: t("builder.step.offline"), oneSlot: t("builder.step.oneSlot"), twoSlot: t("builder.step.twoSlot"), review: t("builder.step.review"),
  };
  const messages = {
    schema: t("builder.validation.schema"), artwork: t("builder.validation.artwork"),
    credit: t("builder.validation.credit"), weekDates: t("builder.validation.weekDates"),
    offline: t("builder.validation.offline"), required: t("builder.validation.required"),
    style: t("builder.validation.style"), font: t("builder.validation.font"),
    size: t("builder.validation.size"), weight: t("builder.validation.weight"),
    color: t("builder.validation.color"), format: t("builder.validation.format"),
    textFormat: t("builder.validation.textFormat"), icon: t("builder.validation.icon"),
    scale: t("builder.validation.scale"), binding: t("builder.validation.binding"),
  };
  if (!issues.length) return <p className="rounded border border-green-200 bg-green-50 p-3 text-green-800">{t("builder.review.valid")}</p>;
  return <div className="space-y-2 rounded border border-red-200 bg-red-50 p-3 text-red-700">
    <h4 className="font-semibold">{t("builder.review.issueCount", { count: issues.length })}</h4>
    <ul className="space-y-3">{issues.map((issue, index) => {
      const target = targets.find(target => target.path === issue.target);
      const location = target ? metadataTargetLabel(target, labels) : issue.day ? labels[issue.day] : null;
      return <li key={`${issue.path}:${issue.code}:${index}`} className="space-y-1">
        <p className="font-semibold">{steps[issue.step]}{location && ` · ${location}`}</p>
        <p>{messages[issue.code]}</p>
        <p className="break-all font-mono text-xs">{issue.path}</p>
        <Button variant="outline" size="sm" onClick={() => {
          endMetadataMerge();
          if (issue.day) useMetadataBuilderStore.setState({ selectedDay: issue.day, previewDay: issue.day });
          useMetadataBuilderStore.getState().setStep(issue.step);
          useMetadataEditingStore.getState().select(issue.target ?? null);
        }}>{t("builder.review.fix")}</Button>
      </li>;
    })}</ul>
  </div>;
}
